import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { EmptyState } from "@/components/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { getMyPublications } from "@/lib/writer.functions";

export const Route = createFileRoute("/_authenticated/studio/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics — Creator Studio — Quibblies" },
      {
        name: "description",
        content:
          "Private reading analytics for your Quibblies publications: views, likes, bookmarks and comments per piece.",
      },
      { property: "og:title", content: "Analytics — Quibblies Creator Studio" },
      { property: "og:description", content: "Private reading analytics for your writing." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const fetchPublications = useServerFn(getMyPublications);
  const { data, isLoading } = useQuery({
    queryKey: ["studio-publications"],
    queryFn: () => fetchPublications(),
  });

  if (isLoading) return <Skeleton className="h-64 w-full rounded-2xl" />;

  const rows = (data ?? []).filter((row) => row.status === "published");
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No analytics yet"
        description="Once a publication is approved and published, its reading numbers appear here. Only you can see them."
      />
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-border/60 bg-background/60">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">Performance of your published writing</caption>
        <thead className="border-b border-border/60 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th scope="col" className="px-4 py-3">Publication</th>
            <th scope="col" className="px-4 py-3 text-right">Views</th>
            <th scope="col" className="px-4 py-3 text-right">Likes</th>
            <th scope="col" className="px-4 py-3 text-right">Bookmarks</th>
            <th scope="col" className="px-4 py-3 text-right">Comments</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-border/40 last:border-0">
              <th scope="row" className="px-4 py-3 font-medium text-foreground">
                {row.title}
              </th>
              <td className="px-4 py-3 text-right text-muted-foreground">{row.view_count}</td>
              <td className="px-4 py-3 text-right text-muted-foreground">{row.like_count}</td>
              <td className="px-4 py-3 text-right text-muted-foreground">{row.bookmark_count}</td>
              <td className="px-4 py-3 text-right text-muted-foreground">{row.comment_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
